import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCheck } from "@fortawesome/free-solid-svg-icons";

type Props = {
  gameId: string;
  tooltip?: string;
};

export default function WatchlistButton({ gameId, tooltip }: Props) {
  const { data: session } = useSession();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const token = (session?.user as any)?.accessToken;

  useEffect(() => {
    if (!token || !gameId) {
      setChecking(false);
      return;
    }
    fetch(`${process.env.BACKEND_BASE_URL}/notifications/watchlist/${gameId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setInWatchlist(data.data.inWatchlist);
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [token, gameId]);

  if (!token || checking) return null;

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (inWatchlist) {
        await fetch(
          `${process.env.BACKEND_BASE_URL}/notifications/watchlist/${gameId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setInWatchlist(false);
      } else {
        await fetch(
          `${process.env.BACKEND_BASE_URL}/notifications/watchlist/${gameId}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setInWatchlist(true);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  // On Air: a plate button the height of `.btn-signal`. Off: a surface plate;
  // on (following): the signal plate with a check, announced by aria-pressed.
  return (
    <div className="relative group shrink-0">
      <button
        type="button"
        onClick={toggle}
        disabled={loading}
        aria-pressed={inWatchlist}
        className={`inline-flex h-10 items-center justify-center gap-2 px-4 font-display text-[15px] font-semibold transition-colors duration-150 disabled:cursor-wait disabled:opacity-70 ${
          inWatchlist
            ? "bg-signal text-white hover:bg-[#0047CC]"
            : "bg-theme-surface text-theme-text hover:text-signal dark:hover:text-[#8DB4FF]"
        }`}
      >
        <FontAwesomeIcon icon={inWatchlist ? faCheck : faBell} className="text-sm" />
        {inWatchlist ? "Followed" : "Follow"}
      </button>
      {tooltip && (
        <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-max max-w-[220px] -translate-x-1/2 bg-stage-deep px-3 py-1.5 text-center text-xs leading-snug text-white opacity-0 shadow-float transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {tooltip}
        </div>
      )}
    </div>
  );
}
